import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import styled from 'styled-components';
import { MdAdd, MdBookmarks, MdDeleteOutline, MdDoneAll, MdEdit, MdLink, MdMenuBook, MdPerson, MdPersonOutline, MdSearch, MdWarningAmber } from 'react-icons/md';
import DashboardLayout from '../../components/DashboardLayout';
import {
  createDueCourseMapping,
  deleteStaffDueCourseMappings,
  getDueCourseMappingOptions,
  listDueCourseMappings,
  updateStaffDueCourseMappings,
} from '../../services/dueCourseMappingService';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Hero = styled.section`
  border-radius: 24px;
  padding: 28px;
  color: #f8fafc;
  background:
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.22), transparent 28%),
    linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #0f766e 100%);
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
`;

const HeroEyebrow = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #bae6fd;
`;

const HeroTitle = styled.h1`
  margin: 10px 0 0;
  font-size: 32px;
  font-weight: 800;
`;

const HeroText = styled.p`
  margin: 12px 0 0;
  max-width: 760px;
  font-size: 14px;
  line-height: 1.7;
  color: rgba(226, 232, 240, 0.94);
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(226, 232, 240, 0.16);
  font-size: 13px;
  font-weight: 700;
`;

const SummaryGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const SummaryCard = styled.article`
  border-radius: 18px;
  padding: 20px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-top: 3px solid ${(props) => props.$accent || '#2563eb'};
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
`;

const SummaryCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SummaryIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$bg || '#eff6ff'};
  color: ${(props) => props.$color || '#2563eb'};
  flex-shrink: 0;
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #64748b;
  padding-top: 2px;
`;

const SummaryValue = styled.div`
  font-size: 32px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1;
`;

const SummaryHint = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
`;

const SectionCard = styled.section`
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
  overflow: hidden;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid #eef2f7;
`;

const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  padding-left: 12px;
  border-left: 4px solid #2563eb;
`;

const SectionDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: #64748b;
`;

const Toolbar = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 1.1fr) minmax(220px, 0.85fr) minmax(220px, 0.85fr) auto;
  gap: 12px;
  padding: 16px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SearchWrap = styled.div`
  position: relative;
`;

const SearchInputIcon = styled.span`
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  display: inline-flex;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  padding: 0 14px 0 40px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
  }
`;

const Button = styled.button`
  height: 44px;
  border-radius: 12px;
  border: none;
  padding: 0 18px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.15s;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
`;

const PrimaryButton = styled(Button)`
  background: linear-gradient(135deg, #2563eb 0%, #0f766e 100%);
  color: #ffffff;
  box-shadow: 0 6px 18px rgba(37, 99, 235, 0.24);

  &:hover:not(:disabled) {
    opacity: 0.92;
    box-shadow: 0 10px 26px rgba(37, 99, 235, 0.32);
  }
`;

const GhostButton = styled(Button)`
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #94a3b8;
  }
`;

const TableWrap = styled.div`
  padding: 0 0 4px;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 920px;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 13px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  background: #f8fafc;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #0f172a;
  border-bottom: 1px solid #eef2f7;
  vertical-align: top;
`;

const Tr = styled.tr`
  transition: background 0.12s;

  &:nth-child(even) td {
    background: #fcfcfd;
  }

  &:hover td {
    background: #f0f6ff !important;
  }
`;

const StaffName = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
`;

const SubText = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
`;

const AssignmentBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid #bfdbfe;
`;

const CodeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #dcfce7;
  color: #166534;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid #bbf7d0;
`;

const CourseList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
`;

const ActionRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover {
    background: #f8fafc;
  }
`;

const EditIconButton = styled(IconButton)`
  &:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #1d4ed8;
  }
`;

const DeleteIconButton = styled(IconButton)`
  &:hover {
    background: #fef2f2;
    border-color: #fecaca;
    color: #dc2626;
  }
`;

const EmptyState = styled.div`
  padding: 56px 24px 64px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
  color: #cbd5e1;
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 6px;
`;

const EmptyDesc = styled.div`
  font-size: 13px;
  color: #94a3b8;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.56);
  overflow-y: auto;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 760px;
  max-height: calc(100vh - 40px);
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.26);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: auto 0;
`;

const ModalHeader = styled.div`
  padding: 28px 24px 18px;
  border-bottom: 1px solid #eef2f7;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #2563eb 0%, #0f766e 100%);
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
`;

const ModalText = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
`;

const ModalBody = styled.div`
  padding: 22px 24px;
  display: grid;
  gap: 18px;
  overflow-y: auto;
`;

const FieldBlock = styled.div`
  display: grid;
  gap: 8px;
`;

const InlineToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #334155;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`;

const InlineToggleInput = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
`;

const CourseSelectionCard = styled.div`
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  overflow: hidden;
`;

const CourseSelectionToolbar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  border-bottom: 1px solid #eef2f7;
`;

const ToolbarSearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const CourseSearchWrap = styled.div`
  position: relative;
`;

const CourseSearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  display: inline-flex;
`;

const CourseSearchInput = styled.input`
  width: 100%;
  height: 42px;
  border-radius: 12px;
  border: 1px solid #cbd5e1;
  padding: 0 14px 0 40px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.12);
  }
`;

const CourseSelectionMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 16px 14px;
  font-size: 12px;
  color: #475569;
`;

const MetaTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 999px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  font-weight: 700;
`;

const CourseTableWrap = styled.div`
  max-height: 300px;
  overflow: auto;
`;

const CourseTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const CourseTh = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 12px 14px;
  text-align: left;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #475569;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
`;

const CourseTd = styled.td`
  padding: 12px 14px;
  border-bottom: 1px solid #eef2f7;
  font-size: 13px;
  color: #0f172a;
  vertical-align: top;
`;

const CourseName = styled.div`
  font-weight: 700;
`;

const CourseHint = styled.div`
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
`;

const PreviewCard = styled.div`
  border-radius: 16px;
  padding: 16px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  display: grid;
  gap: 12px;
`;

const PreviewTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: #0f172a;
`;

const PreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #334155;
`;

const ModalFooter = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #eef2f7;
`;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 12,
    borderColor: state.isFocused ? '#2563eb' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 4px rgba(37, 99, 235, 0.12)' : 'none',
    '&:hover': { borderColor: '#2563eb' },
  }),
  valueContainer: (base) => ({ ...base, padding: '2px 12px' }),
  menu: (base) => ({ ...base, zIndex: 30, borderRadius: 12, overflow: 'hidden' }),
  indicatorSeparator: () => ({ display: 'none' }),
};

function buildStaffOption(staff) {
  const labelParts = [staff.name];
  if (staff.employeeNumber) {
    labelParts.push(staff.employeeNumber);
  }
  return {
    value: staff.id,
    label: labelParts.join(' • '),
    meta: staff,
  };
}

export default function DueCourseMapping() {
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [summary, setSummary] = useState({ totalMappings: 0, uniqueStaff: 0, uniqueCourseCodes: 0 });
  const [options, setOptions] = useState({ staff: [], dueCourses: [] });
  const [search, setSearch] = useState('');
  const [staffFilter, setStaffFilter] = useState(null);
  const [courseFilter, setCourseFilter] = useState(null);
  const [modalState, setModalState] = useState({ open: false, mode: 'create', record: null });
  const [selectedStaffOption, setSelectedStaffOption] = useState(null);
  const [selectedCourseCodes, setSelectedCourseCodes] = useState([]);
  const [dueCourseSearch, setDueCourseSearch] = useState('');
  const [showOnlyUnassignedCourses, setShowOnlyUnassignedCourses] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [mappingData, optionData] = await Promise.all([
        listDueCourseMappings(),
        getDueCourseMappingOptions(),
      ]);
      setMappings(mappingData?.data || []);
      setSummary(mappingData?.summary || { totalMappings: 0, uniqueStaff: 0, uniqueCourseCodes: 0 });
      setOptions({
        staff: optionData?.staff || [],
        dueCourses: optionData?.dueCourses || [],
      });
    } catch (loadError) {
      setError(loadError?.response?.data?.message || 'Failed to load due course mappings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const staffOptions = useMemo(() => options.staff.map(buildStaffOption), [options.staff]);
  const dueCourseOptions = useMemo(() => (
    options.dueCourses.map((course) => ({
      value: course.dueCourseCode,
      label: course.sourceName,
    }))
  ), [options.dueCourses]);

  const selectedCourseSet = useMemo(() => new Set(selectedCourseCodes), [selectedCourseCodes]);

  const assignedDueCourseCodeSet = useMemo(() => new Set(
    mappings
      .map((record) => record.dueCourse)
      .filter(Boolean)
  ), [mappings]);

  const filteredDueCourses = useMemo(() => {
    const query = dueCourseSearch.trim().toLowerCase();
    return options.dueCourses.filter((course) => {
      const matchesAssignmentFilter = (
        !(modalState.mode === 'create' && showOnlyUnassignedCourses)
        || !assignedDueCourseCodeSet.has(course.dueCourseCode)
      );
      const matchesSearch = !query || String(course.sourceName || '').toLowerCase().includes(query);
      return matchesAssignmentFilter && matchesSearch;
    });
  }, [assignedDueCourseCodeSet, dueCourseSearch, modalState.mode, options.dueCourses, showOnlyUnassignedCourses]);

  const visibleCourseCodes = useMemo(() => ([...new Set(
    filteredDueCourses.map((course) => course.dueCourseCode).filter(Boolean)
  )]), [filteredDueCourses]);

  const allVisibleSelected = useMemo(() => (
    visibleCourseCodes.length > 0
    && visibleCourseCodes.every((courseCode) => selectedCourseSet.has(courseCode))
  ), [selectedCourseSet, visibleCourseCodes]);

  const groupedMappings = useMemo(() => {
    const grouped = new Map();

    mappings.forEach((record) => {
      const staffId = record.staff?.id || `unknown-${record.id}`;
      const current = grouped.get(staffId);

      if (current) {
        if (record.dueCourse && !current.dueCourses.includes(record.dueCourse)) {
          current.dueCourses.push(record.dueCourse);
        }
        if (!current.updatedAt || (record.updatedAt && new Date(record.updatedAt) > new Date(current.updatedAt))) {
          current.updatedAt = record.updatedAt;
        }
        return;
      }

      grouped.set(staffId, {
        id: staffId,
        staff: record.staff,
        dueCourses: record.dueCourse ? [record.dueCourse] : [],
        updatedAt: record.updatedAt,
      });
    });

    return Array.from(grouped.values()).map((record) => ({
      ...record,
      dueCourses: [...record.dueCourses].sort((left, right) => left.localeCompare(right)),
      assignedCount: record.dueCourses.length,
    }));
  }, [mappings]);

  const filteredMappings = useMemo(() => {
    const query = search.trim().toLowerCase();
    return groupedMappings.filter((record) => {
      const matchesSearch = !query || [
        record.staff?.name,
        record.staff?.employeeNumber,
        ...record.dueCourses,
      ].some((value) => String(value || '').toLowerCase().includes(query));

      const matchesStaff = !staffFilter || record.staff?.id === staffFilter.value;
      const matchesCourse = !courseFilter || record.dueCourses.includes(courseFilter.value);
      return matchesSearch && matchesStaff && matchesCourse;
    });
  }, [courseFilter, groupedMappings, search, staffFilter]);

  const unassignedStaffCount = useMemo(() => {
    const assigned = new Set(mappings.map((record) => record.staff?.id).filter(Boolean));
    return options.staff.filter((staff) => !assigned.has(staff.id)).length;
  }, [mappings, options.staff]);

  const unassignedDueCourseCount = useMemo(() => {
    return options.dueCourses.filter((course) => !assignedDueCourseCodeSet.has(course.dueCourseCode)).length;
  }, [assignedDueCourseCodeSet, options.dueCourses]);

  const resetModal = () => {
    setModalState({ open: false, mode: 'create', record: null });
    setSelectedStaffOption(null);
    setSelectedCourseCodes([]);
    setDueCourseSearch('');
    setShowOnlyUnassignedCourses(false);
    setSubmitting(false);
  };

  const openCreateModal = () => {
    setModalState({ open: true, mode: 'create', record: null });
    setSelectedStaffOption(null);
    setSelectedCourseCodes([]);
    setDueCourseSearch('');
    setShowOnlyUnassignedCourses(true);
  };

  const openEditModal = (record) => {
    setModalState({ open: true, mode: 'edit', record });
    setSelectedStaffOption(staffOptions.find((option) => option.value === record.staff?.id) || null);
    setSelectedCourseCodes(record.dueCourses || []);
    setDueCourseSearch('');
    setShowOnlyUnassignedCourses(false);
  };

  const toggleCourseSelection = (courseCode) => {
    if (!courseCode) {
      return;
    }

    setSelectedCourseCodes((current) => {
      if (current.includes(courseCode)) {
        return current.filter((value) => value !== courseCode);
      }
      return [...current, courseCode];
    });
  };

  const toggleAllVisible = () => {
    if (!visibleCourseCodes.length) {
      return;
    }

    setSelectedCourseCodes((current) => {
      const next = new Set(current);
      if (allVisibleSelected) {
        visibleCourseCodes.forEach((value) => next.delete(value));
      } else {
        visibleCourseCodes.forEach((value) => next.add(value));
      }
      return [...next];
    });
  };

  const handleSubmit = async () => {
    if (!selectedStaffOption || !selectedCourseCodes.length) {
      await Swal.fire({
        icon: 'warning',
        title: 'Missing selection',
        text: 'Choose a staff member and at least one due course code.',
      });
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        staffId: selectedStaffOption.value,
        dueCourseCodes: selectedCourseCodes,
        dueCourseCode: selectedCourseCodes[0],
      };

      if (modalState.mode === 'edit' && modalState.record?.staff?.id) {
        await updateStaffDueCourseMappings(modalState.record.staff.id, payload);
      } else {
        const response = await createDueCourseMapping(payload);
        await loadData();
        resetModal();
        await Swal.fire({
          icon: 'success',
          title: 'Mappings created',
          text: response?.message || 'Due course mappings were created successfully.',
          timer: 2200,
          showConfirmButton: false,
        });
        return;
      }

      await loadData();
      resetModal();
      await Swal.fire({
        icon: 'success',
        title: 'Mappings updated',
        text: 'Due course assignments were saved successfully.',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (submitError) {
      await Swal.fire({
        icon: 'error',
        title: 'Save failed',
        text: submitError?.response?.data?.message || 'Failed to save due course mapping.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (record) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Delete all mappings?',
      text: `Remove all ${record.assignedCount} due course assignment(s) for ${record.staff?.name}?`,
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#dc2626',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteStaffDueCourseMappings(record.staff?.id);
      await loadData();
      await Swal.fire({
        icon: 'success',
        title: 'Mappings deleted',
        text: 'The due course assignments were removed.',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (deleteError) {
      await Swal.fire({
        icon: 'error',
        title: 'Delete failed',
        text: deleteError?.response?.data?.message || 'Failed to delete due course mapping.',
      });
    }
  };

  return (
    <DashboardLayout title="Due Course Mapping">
      <Page>
        <Hero>
          <HeroEyebrow>Admin Only</HeroEyebrow>
          <HeroTitle>Due Course Mapping</HeroTitle>
          <HeroText>
            Assign staff members to due course codes loaded from student due data. This controls which records appear in My Course Due for each staff member.
          </HeroText>
          <HeroMeta>
            <MetaPill><MdPerson /> {summary.uniqueStaff} mapped staff</MetaPill>
            <MetaPill><MdMenuBook /> {summary.uniqueCourseCodes} mapped due courses</MetaPill>
            <MetaPill><MdLink /> {summary.totalMappings} active mappings</MetaPill>
            <MetaPill><MdMenuBook /> {unassignedDueCourseCount} unassigned due courses</MetaPill>
          </HeroMeta>
        </Hero>

        <SummaryGrid>
          <SummaryCard $accent="#2563eb">
            <SummaryCardTop>
              <SummaryLabel>Total Mappings</SummaryLabel>
              <SummaryIconBox $bg="#eff6ff" $color="#2563eb"><MdLink size={20} /></SummaryIconBox>
            </SummaryCardTop>
            <SummaryValue>{summary.totalMappings}</SummaryValue>
            <SummaryHint>All active staff-to-due-course assignments configured.</SummaryHint>
          </SummaryCard>
          <SummaryCard $accent="#0f766e">
            <SummaryCardTop>
              <SummaryLabel>Mapped Staff</SummaryLabel>
              <SummaryIconBox $bg="#f0fdfa" $color="#0f766e"><MdPerson size={20} /></SummaryIconBox>
            </SummaryCardTop>
            <SummaryValue>{summary.uniqueStaff}</SummaryValue>
            <SummaryHint>Staff linked to at least one due course code.</SummaryHint>
          </SummaryCard>
          <SummaryCard $accent="#7c3aed">
            <SummaryCardTop>
              <SummaryLabel>Due Course Codes</SummaryLabel>
              <SummaryIconBox $bg="#f5f3ff" $color="#7c3aed"><MdMenuBook size={20} /></SummaryIconBox>
            </SummaryCardTop>
            <SummaryValue>{summary.uniqueCourseCodes}</SummaryValue>
            <SummaryHint>Distinct courses mapped from student due records.</SummaryHint>
          </SummaryCard>
          <SummaryCard $accent="#d97706">
            <SummaryCardTop>
              <SummaryLabel>Unassigned Staff</SummaryLabel>
              <SummaryIconBox $bg="#fffbeb" $color="#d97706"><MdPersonOutline size={20} /></SummaryIconBox>
            </SummaryCardTop>
            <SummaryValue>{unassignedStaffCount}</SummaryValue>
            <SummaryHint>Active staff with no due course assignments yet.</SummaryHint>
          </SummaryCard>
          <SummaryCard $accent="#dc2626">
            <SummaryCardTop>
              <SummaryLabel>Unassigned Courses</SummaryLabel>
              <SummaryIconBox $bg="#fef2f2" $color="#dc2626"><MdBookmarks size={20} /></SummaryIconBox>
            </SummaryCardTop>
            <SummaryValue>{unassignedDueCourseCount}</SummaryValue>
            <SummaryHint>Course codes from due data not yet assigned to any staff.</SummaryHint>
          </SummaryCard>
        </SummaryGrid>

        <SectionCard>
          <SectionHeader>
            <SectionHeading>
              <SectionTitle>Manage Mappings</SectionTitle>
              <SectionDescription>Search, filter, and maintain staff ownership for due-course follow-up.</SectionDescription>
            </SectionHeading>
            <PrimaryButton type="button" onClick={openCreateModal}>
              <MdAdd size={18} /> Add Mapping
            </PrimaryButton>
          </SectionHeader>

          {error ? (
            <EmptyState>
              <EmptyIcon><MdWarningAmber size={42} /></EmptyIcon>
              <EmptyTitle>Failed to load</EmptyTitle>
              <EmptyDesc>{error}</EmptyDesc>
            </EmptyState>
          ) : null}

          <Toolbar>
            <SearchWrap>
              <SearchInputIcon><MdSearch size={17} /></SearchInputIcon>
              <SearchInput
                type="text"
                placeholder="Search by staff, employee number, or due course"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </SearchWrap>
            <Select
              isClearable
              placeholder="Filter by staff"
              options={staffOptions}
              value={staffFilter}
              onChange={setStaffFilter}
              styles={selectStyles}
            />
            <Select
              isClearable
              placeholder="Filter by due course"
              options={dueCourseOptions}
              value={courseFilter}
              onChange={setCourseFilter}
              styles={selectStyles}
            />
            <GhostButton
              type="button"
              onClick={() => {
                setSearch('');
                setStaffFilter(null);
                setCourseFilter(null);
              }}
            >
              Clear Filters
            </GhostButton>
          </Toolbar>

          <TableWrap>
            {loading ? (
              <EmptyState>
                <EmptyIcon><MdMenuBook size={42} /></EmptyIcon>
                <EmptyTitle>Loading mappings…</EmptyTitle>
                <EmptyDesc>Please wait while data is being fetched.</EmptyDesc>
              </EmptyState>
            ) : filteredMappings.length === 0 ? (
              <EmptyState>
                <EmptyIcon><MdLink size={42} /></EmptyIcon>
                <EmptyTitle>No mappings found</EmptyTitle>
                <EmptyDesc>No due course mappings match the current filters.</EmptyDesc>
              </EmptyState>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Staff</Th>
                    <Th>Assigned Due Courses</Th>
                    <Th>Updated</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMappings.map((record) => (
                    <Tr key={record.id}>
                      <Td>
                        <StaffName>{record.staff?.name || '—'}</StaffName>
                        <SubText>
                          {[record.staff?.employeeNumber, record.staff?.position].filter(Boolean).join(' • ') || 'No staff metadata'}
                        </SubText>
                      </Td>
                      <Td>
                        <AssignmentBadge><MdLink size={13} /> {record.assignedCount} assigned</AssignmentBadge>
                        <SubText>{record.staff?.type || 'No staff type'}</SubText>
                        {record.dueCourses.length ? (
                          <CourseList>
                            {record.dueCourses.slice(0, 4).map((course) => (
                              <CodeBadge key={course}>{course}</CodeBadge>
                            ))}
                            {record.dueCourses.length > 4 ? (
                              <CodeBadge>+{record.dueCourses.length - 4} more</CodeBadge>
                            ) : null}
                          </CourseList>
                        ) : null}
                      </Td>
                      <Td>
                        <div style={{ fontWeight: 700 }}>
                          {record.updatedAt ? new Date(record.updatedAt).toLocaleDateString('en-GB') : '—'}
                        </div>
                        <SubText>
                          {record.updatedAt ? new Date(record.updatedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '—'}
                        </SubText>
                      </Td>
                      <Td>
                        <ActionRow>
                          <EditIconButton type="button" title="Edit mapping" onClick={() => openEditModal(record)}>
                            <MdEdit size={18} />
                          </EditIconButton>
                          <DeleteIconButton type="button" title="Delete mapping" onClick={() => handleDelete(record)}>
                            <MdDeleteOutline size={18} />
                          </DeleteIconButton>
                        </ActionRow>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableWrap>
        </SectionCard>

        {modalState.open ? (
          <ModalOverlay onClick={resetModal}>
            <ModalCard onClick={(event) => event.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{modalState.mode === 'edit' ? 'Edit Staff Due Course Mapping' : 'Create Due Course Mapping'}</ModalTitle>
                <ModalText>
                  Select the staff member, then choose one or more due course codes loaded from student due data.
                </ModalText>
              </ModalHeader>
              <ModalBody>
                <FieldBlock>
                  <Label>Staff Member</Label>
                  <Select
                    placeholder="Select staff"
                    options={staffOptions}
                    value={selectedStaffOption}
                    onChange={setSelectedStaffOption}
                    isDisabled={modalState.mode === 'edit'}
                    styles={selectStyles}
                  />
                </FieldBlock>

                <FieldBlock>
                  <Label>Select Due Course Codes</Label>
                  <CourseSelectionCard>
                    <CourseSelectionToolbar>
                      <ToolbarSearchRow>
                        <CourseSearchWrap style={{ flex: 1 }}>
                          <CourseSearchIcon><MdSearch size={18} /></CourseSearchIcon>
                          <CourseSearchInput
                            type="text"
                            placeholder="Search due course codes"
                            value={dueCourseSearch}
                            onChange={(event) => setDueCourseSearch(event.target.value)}
                          />
                        </CourseSearchWrap>
                        {modalState.mode === 'create' ? (
                          <InlineToggle>
                            <InlineToggleInput
                              type="checkbox"
                              checked={showOnlyUnassignedCourses}
                              onChange={(event) => setShowOnlyUnassignedCourses(event.target.checked)}
                            />
                            Show only unassigned courses
                          </InlineToggle>
                        ) : null}
                      </ToolbarSearchRow>
                      <GhostButton type="button" onClick={toggleAllVisible} style={{ alignSelf: 'flex-start' }}>
                        <MdDoneAll size={18} /> {allVisibleSelected ? 'Clear Visible' : 'Select All Visible'}
                      </GhostButton>
                    </CourseSelectionToolbar>

                    <CourseSelectionMeta>
                      <MetaTag>{filteredDueCourses.length} visible codes</MetaTag>
                      <MetaTag>{selectedCourseCodes.length} selected</MetaTag>
                    </CourseSelectionMeta>

                    <CourseTableWrap>
                      <CourseTable>
                        <thead>
                          <tr>
                            <CourseTh style={{ width: '56px' }}>Pick</CourseTh>
                            <CourseTh>Due Course Code</CourseTh>
                            <CourseTh>Student Due Records</CourseTh>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredDueCourses.length === 0 ? (
                            <tr>
                              <CourseTd colSpan={3}>No due course codes match the current search.</CourseTd>
                            </tr>
                          ) : (
                            filteredDueCourses.map((course) => {
                              const isChecked = selectedCourseSet.has(course.dueCourseCode);
                              return (
                                <tr key={course.dueCourseCode}>
                                  <CourseTd>
                                    <Checkbox
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleCourseSelection(course.dueCourseCode)}
                                    />
                                  </CourseTd>
                                  <CourseTd>
                                    <CourseName>{course.sourceName}</CourseName>
                                  </CourseTd>
                                  <CourseTd>
                                    <CourseHint>{course.dueStudentCount} due record(s)</CourseHint>
                                  </CourseTd>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </CourseTable>
                    </CourseTableWrap>
                  </CourseSelectionCard>
                </FieldBlock>

                <PreviewCard>
                  <PreviewTitle>Assignment Preview</PreviewTitle>
                  <PreviewRow>
                    <MdPerson size={18} />
                    <span>{selectedStaffOption?.meta?.name || 'No staff selected'}</span>
                  </PreviewRow>
                  <PreviewRow>
                    <MdMenuBook size={18} />
                    <span>
                      {selectedCourseCodes.length
                        ? `${selectedCourseCodes.length} due course code${selectedCourseCodes.length > 1 ? 's' : ''} selected`
                        : 'No due course code selected'}
                    </span>
                  </PreviewRow>
                  {selectedCourseCodes.length ? (
                    <PreviewRow>
                      <MdLink size={18} />
                      <span>
                        {selectedCourseCodes.slice(0, 3).join(', ')}
                        {selectedCourseCodes.length > 3 ? ` +${selectedCourseCodes.length - 3} more` : ''}
                      </span>
                    </PreviewRow>
                  ) : null}
                </PreviewCard>
              </ModalBody>
              <ModalFooter>
                <GhostButton type="button" onClick={resetModal}>Cancel</GhostButton>
                <PrimaryButton type="button" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Saving...' : modalState.mode === 'edit' ? 'Save Changes' : 'Create Mapping'}
                </PrimaryButton>
              </ModalFooter>
            </ModalCard>
          </ModalOverlay>
        ) : null}
      </Page>
    </DashboardLayout>
  );
}